// src/pages/Spin.js
// Hidden prize-wheel promo page — reached only via a direct QR-code link at
// in-person events. Never linked in nav/footer, excluded from sitemap.xml,
// and marked noindex via usePageTitle so it can't surface in search.
//
// Flow: email (+ marketing consent) -> follow Instagram -> spin -> reveal
// prize + redemption code. One spin per email, ever: the email is checked
// against the Google Sheet (see src/lib/spinSheet.js) before the wheel is
// shown, and the spin is logged the moment it starts so reloading mid-spin
// can't earn a second go.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Copy, Check, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';
import PrizeWheel, { computeTargetRotation, SEGMENT_ANGLE, SPIN_DURATION_MS, SPIN_EASING } from '../components/PrizeWheel';
import { PRIZES, pickWeightedPrize, generateRedemptionCode, todayKey } from '../lib/spinPrizes';
import { checkEmailEntered, logSpinEntry } from '../lib/spinSheet';
import { createCubicBezierEasing } from '../lib/bezierEasing';
import { playSpinTick, playWinChime, setSpinAudioMuted } from '../lib/spinAudio';

const INSTAGRAM_URL = 'https://www.instagram.com/maisondoudofficial';
const STORAGE_KEY = 'maisondoud_spin_entry';
const MUTE_STORAGE_KEY = 'maisondoud_spin_muted';
const SPIN_ANIMATION_MS = SPIN_DURATION_MS + 100;
// How long the wheel stays on screen, confetti bursting from it, before the
// view switches to the result/code reveal.
const CELEBRATE_MS = 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same curve the wheel's CSS transition animates with, so tick sounds land
// on the exact frame the wheel visually passes each slice boundary.
const spinEasing = createCubicBezierEasing(...SPIN_EASING);

const Spin = () => {
    usePageTitle('Spin & Win', 'A private prize wheel for MAISON D\'OUD insiders.', undefined, { noindex: true });

    const [step, setStep] = useState('email'); // email | instagram | wheel | result | blocked
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [hasClickedFollow, setHasClickedFollow] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [spinning, setSpinning] = useState(false);
    const [wonPrize, setWonPrize] = useState(null);
    const [code, setCode] = useState('');
    const [alreadySpun, setAlreadySpun] = useState(false);
    const [copied, setCopied] = useState(false);
    const [muted, setMuted] = useState(false);
    const [celebrating, setCelebrating] = useState(false);
    const [celebrateKey, setCelebrateKey] = useState(0);
    const spinTimeoutRef = useRef(null);
    const celebrateTimeoutRef = useRef(null);
    const tickRafRef = useRef(null);

    // If this browser already spun today, skip straight to their result
    // instead of letting them spin again.
    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
            if (stored && stored.date === todayKey()) {
                const prize = PRIZES.find((p) => p.id === stored.prizeId);
                setWonPrize(prize || { label: stored.prizeLabel, shortLabel: '' });
                setCode(stored.code);
                setEmail(stored.email || '');
                setAlreadySpun(true);
                setStep('result');
            }

            const storedMuted = localStorage.getItem(MUTE_STORAGE_KEY) === '1';
            setMuted(storedMuted);
            setSpinAudioMuted(storedMuted);
        } catch {
            // Ignore malformed/blocked localStorage — just show the normal flow.
        }
        return () => {
            clearTimeout(spinTimeoutRef.current);
            clearTimeout(celebrateTimeoutRef.current);
            if (tickRafRef.current) cancelAnimationFrame(tickRafRef.current);
        };
    }, []);

    const handleToggleMute = () => {
        const next = !muted;
        setMuted(next);
        setSpinAudioMuted(next);
        try {
            localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
        } catch {
            // Storage unavailable — mute state just won't persist across visits.
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (checkingEmail) return;
        if (!EMAIL_RE.test(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setEmailError('');
        setCheckingEmail(true);
        try {
            const alreadyEntered = await checkEmailEntered(email);
            setStep(alreadyEntered ? 'blocked' : 'instagram');
        } catch {
            // Fail closed: without the sheet check we can't enforce one spin
            // per email, so ask them to retry rather than letting them through.
            setEmailError('We couldn\'t verify your email — please check your connection and try again.');
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleFollowClick = () => {
        window.open(INSTAGRAM_URL, '_blank', 'noopener,noreferrer');
        setHasClickedFollow(true);
    };

    const handleSpinClick = () => {
        if (spinning || celebrating || step !== 'wheel') return;

        const prize = pickWeightedPrize();
        const prizeIndex = PRIZES.indexOf(prize);
        const startRotation = rotation;
        const nextRotation = computeTargetRotation(prizeIndex, startRotation);
        const delta = nextRotation - startRotation;
        const newCode = generateRedemptionCode(prize);

        setSpinning(true);
        setRotation(nextRotation);
        setWonPrize(prize);

        // Record the spin up front (locally and in the sheet) so closing or
        // reloading the page mid-spin doesn't grant a fresh spin.
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                date: todayKey(),
                email,
                prizeId: prize.id,
                prizeLabel: prize.label,
                code: newCode,
            }));
        } catch {
            // Storage unavailable (private browsing, etc.) — the sheet still
            // blocks this email from spinning again.
        }
        const logPromise = logSpinEntry({ email, prize: prize.label, code: newCode })
            .catch(() => ({ duplicate: false }));

        // Drive tick sounds off the wheel's actual eased position each frame,
        // rather than a fixed interval, so the clicking slows exactly as the
        // wheel does and lands in sync with where it visually stops.
        const startTime = performance.now();
        let lastBoundary = Math.floor(startRotation / SEGMENT_ANGLE);
        const tickLoop = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / SPIN_DURATION_MS, 1);
            const currentRotation = startRotation + delta * spinEasing(progress);
            const boundary = Math.floor(currentRotation / SEGMENT_ANGLE);
            if (boundary !== lastBoundary) {
                playSpinTick(1 - progress * 0.6);
                lastBoundary = boundary;
            }
            if (progress < 1) {
                tickRafRef.current = requestAnimationFrame(tickLoop);
            }
        };
        tickRafRef.current = requestAnimationFrame(tickLoop);

        spinTimeoutRef.current = setTimeout(async () => {
            // The email passed the sheet check before the wheel, so this only
            // trips if the same email spun elsewhere in the meantime.
            const { duplicate } = await logPromise;
            setSpinning(false);
            if (duplicate) {
                try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
                setWonPrize(null);
                setStep('blocked');
                return;
            }

            setCode(newCode);
            setCelebrating(true);
            playWinChime();
            setCelebrateKey((k) => k + 1);

            // Let the confetti burst out of the wheel for a beat before
            // swapping to the result/code reveal.
            celebrateTimeoutRef.current = setTimeout(() => {
                setCelebrating(false);
                setStep('result');
            }, CELEBRATE_MS);
        }, SPIN_ANIMATION_MS);
    };

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            toast.success('Code copied');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Could not copy — please copy it manually.');
        }
    };

    return (
        <div className="spin-page-root">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Tenor+Sans&display=swap');

                .spin-page-root {
                    min-height: 100vh;
                    background: #0a0a0a;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 32px 20px;
                    position: relative;
                    overflow: hidden;
                }
                .spin-page-orb-1 {
                    position: absolute;
                    top: -120px;
                    right: -120px;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(206,172,143,0.07) 0%, transparent 70%);
                    pointer-events: none;
                }
                .spin-page-orb-2 {
                    position: absolute;
                    bottom: -120px;
                    left: -120px;
                    width: 420px;
                    height: 420px;
                    background: radial-gradient(circle, rgba(206,172,143,0.05) 0%, transparent 70%);
                    pointer-events: none;
                }
                .spin-page-card {
                    width: 100%;
                    max-width: 440px;
                    text-align: center;
                    position: relative;
                    z-index: 1;
                }
                .spin-page-wordmark {
                    font-family: 'Tenor Sans', serif;
                    color: #CEAC8F;
                    font-size: clamp(18px, 4vw, 22px);
                    letter-spacing: 0.25em;
                    font-weight: 400;
                    margin: 0 0 8px;
                }
                .spin-page-eyebrow {
                    color: rgba(206,172,143,0.6);
                    font-size: 10px;
                    letter-spacing: 0.35em;
                    text-transform: uppercase;
                    margin: 0 0 32px;
                }
                .spin-page-box {
                    border: 1px solid rgba(206,172,143,0.15);
                    border-radius: 12px;
                    padding: 40px 28px;
                    background: rgba(255,255,255,0.02);
                    backdrop-filter: blur(10px);
                }
                .spin-page-box-wheel {
                    padding: 32px 10px 36px;
                }
                .spin-page-heading {
                    font-family: 'Tenor Sans', serif;
                    color: rgba(255,255,255,0.95);
                    font-size: clamp(24px, 5vw, 30px);
                    font-weight: 400;
                    letter-spacing: 0.03em;
                    margin: 0 0 14px;
                }
                .spin-page-desc {
                    color: rgba(255,255,255,0.45);
                    font-size: 14px;
                    line-height: 1.7;
                    margin: 0 0 28px;
                }
                .spin-page-input {
                    width: 100%;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(206,172,143,0.25);
                    border-radius: 8px;
                    padding: 14px 16px;
                    color: #fff;
                    font-size: 15px;
                    outline: none;
                }
                .spin-page-input::placeholder { color: rgba(255,255,255,0.3); }
                .spin-page-input:focus { border-color: #CEAC8F; }
                .spin-page-consent {
                    color: rgba(255,255,255,0.35);
                    font-size: 11.5px;
                    line-height: 1.6;
                    margin: 14px 0 22px;
                    text-align: left;
                }
                .spin-page-consent a {
                    color: rgba(206,172,143,0.8);
                    text-decoration: underline;
                }
                .spin-page-error {
                    color: #f87171;
                    font-size: 12px;
                    text-align: left;
                    margin: -16px 0 16px;
                }
                .spin-page-btn {
                    width: 100%;
                    background: #CEAC8F;
                    color: #0a0a0a;
                    border: none;
                    padding: 15px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 14px;
                    letter-spacing: 0.03em;
                    cursor: pointer;
                    transition: background-color 0.2s ease, opacity 0.2s ease;
                }
                .spin-page-btn:hover:not(:disabled) { background: #d4b896; }
                .spin-page-btn:disabled { opacity: 0.45; cursor: default; }
                .spin-page-btn-outline {
                    width: 100%;
                    background: transparent;
                    color: #CEAC8F;
                    border: 1px solid rgba(206,172,143,0.4);
                    padding: 15px;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 13px;
                    letter-spacing: 0.03em;
                    cursor: pointer;
                    margin-top: 12px;
                    transition: border-color 0.2s ease, color 0.2s ease;
                }
                .spin-page-btn-outline:hover { border-color: #CEAC8F; }
                .spin-page-btn-ig {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                .spin-page-code-box {
                    background: rgba(206,172,143,0.08);
                    border: 1px solid rgba(206,172,143,0.3);
                    border-radius: 10px;
                    padding: 18px 20px;
                    margin: 22px 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }
                .spin-page-code-value {
                    font-family: 'Tenor Sans', serif;
                    color: #CEAC8F;
                    font-size: clamp(18px, 5vw, 22px);
                    letter-spacing: 0.15em;
                }
                .spin-page-copy-btn {
                    background: none;
                    border: none;
                    color: #CEAC8F;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    padding: 6px;
                    flex-shrink: 0;
                }
                .spin-page-footer {
                    color: rgba(255,255,255,0.15);
                    font-size: 10px;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    margin-top: 32px;
                }
            `}</style>

            <div className="spin-page-orb-1" />
            <div className="spin-page-orb-2" />

            <div className="spin-page-card">
                <h1 className="spin-page-wordmark">MAISON D'OUD</h1>
                <p className="spin-page-eyebrow">For insiders only</p>

                {step === 'email' && (
                    <div className="spin-page-box">
                        <h2 className="spin-page-heading">Spin &amp; Win</h2>
                        <p className="spin-page-desc">
                            Enter your email for a shot at the wheel — up to 25% off, free
                            samples, a mystery prize and more. One spin per email.
                        </p>
                        <form onSubmit={handleEmailSubmit}>
                            <input
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                className="spin-page-input"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                                required
                            />
                            <p className="spin-page-consent">
                                By entering your email, you agree to receive marketing emails
                                from MAISON D'OUD. You can unsubscribe at any time. See our{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                            </p>
                            {emailError && <p className="spin-page-error">{emailError}</p>}
                            <button type="submit" className="spin-page-btn" disabled={checkingEmail}>
                                {checkingEmail ? 'Checking…' : 'Continue'}
                            </button>
                        </form>
                    </div>
                )}

                {step === 'instagram' && (
                    <div className="spin-page-box">
                        <h2 className="spin-page-heading">One Quick Step</h2>
                        <p className="spin-page-desc">
                            Follow us on Instagram to unlock your spin.
                        </p>
                        <button type="button" onClick={handleFollowClick} className="spin-page-btn spin-page-btn-ig">
                            <Instagram className="w-4 h-4" />
                            Follow @maisondoudofficial
                        </button>
                        <button
                            type="button"
                            onClick={() => setStep('wheel')}
                            disabled={!hasClickedFollow}
                            className="spin-page-btn-outline"
                            style={{ opacity: hasClickedFollow ? 1 : 0.4, cursor: hasClickedFollow ? 'pointer' : 'default' }}
                        >
                            I've Followed — Unlock My Spin
                        </button>
                    </div>
                )}

                {step === 'wheel' && (
                    <div className="spin-page-box spin-page-box-wheel">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <h2 className="spin-page-heading" style={{ margin: 0 }}>Spin the Wheel</h2>
                            <button
                                type="button"
                                onClick={handleToggleMute}
                                aria-label={muted ? 'Unmute sound' : 'Mute sound'}
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(206,172,143,0.6)',
                                    cursor: 'pointer',
                                    padding: 6,
                                }}
                            >
                                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="spin-page-desc" style={{ marginTop: 14 }}>
                            {celebrating ? 'You won!' : 'Tap the wheel for your instant prize.'}
                        </p>
                        <PrizeWheel
                            rotation={rotation}
                            spinning={spinning}
                            onSpinClick={handleSpinClick}
                            spinDisabled={spinning || celebrating}
                            celebrateKey={celebrateKey}
                            celebrating={celebrating}
                        />
                    </div>
                )}

                {step === 'result' && wonPrize && (
                    <div className="spin-page-box">
                        <h2 className="spin-page-heading">
                            {alreadySpun ? 'You Already Won Today' : 'You Won!'}
                        </h2>
                        <p className="spin-page-desc" style={{ color: '#CEAC8F', marginBottom: 4 }}>
                            {wonPrize.label}
                        </p>
                        <div className="spin-page-code-box">
                            <span className="spin-page-code-value">{code}</span>
                            <button type="button" onClick={handleCopyCode} className="spin-page-copy-btn" aria-label="Copy code">
                                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                            </button>
                        </div>

                        <p className="spin-page-desc" style={{ fontSize: 12 }}>
                            Show this code at checkout to claim your prize. Any issues, please
                            contact us via Instagram or email us at{' '}
                            <a href="mailto:support@maisondoud.com">support@maisondoud.com</a>.
                        </p>

                        <Link to="/" className="spin-page-btn-outline" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                            Continue to Website
                        </Link>
                    </div>
                )}

                {step === 'blocked' && (
                    <div className="spin-page-box">
                        <h2 className="spin-page-heading">Already Entered</h2>
                        <p className="spin-page-desc">
                            This email has already spun the wheel. It's one spin per
                            email, but we'll be in touch with more offers soon.
                        </p>
                        <Link to="/" className="spin-page-btn-outline" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                            Continue to Website
                        </Link>
                    </div>
                )}

                <p className="spin-page-footer">© {new Date().getFullYear()} MAISON D'OUD</p>
            </div>
        </div>
    );
};

export default Spin;
